/**
 * Linha da fila PA: ações compactas + Iniciar/Retomar.
 */
import { Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { PlayArrow, Replay } from "@mui/icons-material";
import { useAppointmentActions } from "@/components/schedule/hooks/useAppointmentActions";
import { VerifyResponsibleDialog } from "../VerifyResponsibleDialog";
import { WarningAppointment } from "@/pages/schedule/components/WarningAppointment";
import { useContractContext } from "@/contexts/ContractContext";
import { ProntoAtendimentoQueueActions } from "./ProntoAtendimentoQueueActions";
import type { ProntoQueueRow } from "./mapProntoAppointmentToQueueItem";

interface ProntoAtendimentoQueueRowProps {
  row: ProntoQueueRow;
  setRefetch?: (value: boolean | ((prev: boolean) => boolean)) => void;
  /** Visão clínica: oculta Iniciar/Retomar (não dispara Encounter). */
  hideStartAction?: boolean;
}

export function ProntoAtendimentoQueueRow({
  row,
  setRefetch,
  hideStartAction = false,
}: ProntoAtendimentoQueueRowProps) {
  const { currentContract } = useContractContext();
  const hasSubscriptionFluxPay =
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: any) =>
        coding.system?.includes("contract-type") &&
        coding.value === "fluxPayPlan",
    );

  const appointment = row.actionAppointment;
  const fhirStatus = appointment.status || appointment.originalData?.status || "";
  const inProgress = row.status === "in_progress";
  const paymentPending = fhirStatus === "pending";
  const isCancelled = fhirStatus === "cancelled";
  // Espelha ScheduleQueueActions: Iniciar só com status booked
  const isBooked = fhirStatus === "booked";
  const showStartService = isBooked && !inProgress;

  const statusChip = inProgress
    ? { label: "Em andamento", color: "warning" as const, variant: "filled" as const }
    : paymentPending
      ? {
          label: "Pendente de pagamento",
          color: "warning" as const,
          variant: "filled" as const,
        }
      : isCancelled
        ? { label: "Cancelado", color: "error" as const, variant: "filled" as const }
        : {
            label: "Aguardando",
            color: "primary" as const,
            variant: "outlined" as const,
          };

  const {
    startServiceLoading,
    handleStartService,
    verifyDialog,
    setVerifyDialog,
    fetchRelated,
    related,
    warningAction,
    showPaymentWarning,
    setShowPaymentWarning,
    fetchPayment,
  } = useAppointmentActions({
    appointment,
    setRefetch,
  });

  const handleRefetch = () => setRefetch?.(true);

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 160 }}>
          <Typography variant="body1" fontWeight={600}>
            {row.patientName}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {row.timeLabel}
            </Typography>
            <Chip
              size="small"
              label={statusChip.label}
              color={statusChip.color}
              variant={statusChip.variant}
            />
          </Stack>
        </Stack>

        <ProntoAtendimentoQueueActions
          appointment={appointment}
          onRefetch={handleRefetch}
        />

        {/* Retomar: em andamento; Iniciar: somente booked (igual agenda) */}
        {hideStartAction ? null : inProgress ? (
          <Button
            variant="outlined"
            startIcon={<Replay />}
            onClick={() => void handleStartService(appointment)}
            disabled={startServiceLoading}
            sx={{ textTransform: "none" }}
          >
            {startServiceLoading ? "Abrindo..." : "Retomar"}
          </Button>
        ) : showStartService ? (
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={() => void handleStartService(appointment)}
            disabled={startServiceLoading}
            sx={{ textTransform: "none" }}
          >
            {startServiceLoading ? "Iniciando..." : "Iniciar"}
          </Button>
        ) : null}
      </Paper>

      {showPaymentWarning && (
        <WarningAppointment
          isOpen={showPaymentWarning}
          handleClose={() => setShowPaymentWarning(false)}
          onConfirm={() => {
            setShowPaymentWarning(false);
            if (warningAction === "startService") {
              void handleStartService(appointment, true);
            }
          }}
          appointmentDescription="Esta consulta"
          appointment={appointment?.originalData}
          hasSubscriptionFluxPay={hasSubscriptionFluxPay}
          onPaymentSent={fetchPayment}
        />
      )}

      {verifyDialog && (
        <VerifyResponsibleDialog
          handleClose={() => setVerifyDialog(false)}
          isOpen={true}
          loading={false}
          setLoading={() => {}}
          dataAppointment={appointment?.originalData}
          related={related}
          fetchRelated={fetchRelated}
          patientId={
            typeof appointment?.originalData?.participant?.[0]?.actor
              ?.reference === "string"
              ? appointment.originalData.participant[0].actor.reference
                  .split("-")
                  .slice(1)
                  .join("-")
              : undefined
          }
        />
      )}
    </>
  );
}
